import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { HealthChartsProps, HealthCheck } from "../../types";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { authHeaders } from "../../lib/auth";

export const HealthCharts: React.FC<HealthChartsProps> = ({
  refreshTrigger,
}) => {
  const [rawData, setRawData] = useState<HealthCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [metric, setMetric] = useState("all");
  const [mode, setMode] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const chartLegend = [
    { key: "bp", label: "Sistolik / Diastolik", color: "#dc2626" },
    { key: "temperature", label: "Suhu", color: "#facc15" },
    { key: "heart_rate", label: "Detak Jantung", color: "#f43f5e" },
    { key: "sugar", label: "Gula", color: "#16a34a" },
    { key: "cholesterol", label: "Kolesterol", color: "#9333ea" },
    { key: "urid_acid", label: "Asam Urat", color: "#f97316" },
    { key: "weight", label: "Berat Badan", color: "#0ea5e9" },
  ];

  useEffect(() => {
    const fetchChecks = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_BASE}/api/health/checks`,
          { headers: authHeaders() },
        );
        const json = await res.json();
        setRawData(json);
      } catch (err) {
        console.error("❌ Fetch health checks error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchChecks();
  }, [refreshTrigger]);

  const filtered = rawData.filter((row) => {
    const matchName = row.employee_name
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const rowDate = new Date(row.measured_at);

    const matchStart = startDate
      ? rowDate >= new Date(startDate + "-01T00:00:00")
      : true;

    const matchEnd = endDate
      ? rowDate <=
        (() => {
          const d = new Date(endDate + "-01T00:00:00");
          d.setMonth(d.getMonth() + 1, 0);
          d.setHours(23, 59, 59, 999);
          return d;
        })()
      : true;

    return matchName && matchStart && matchEnd;
  });

  const normalized = filtered.map((row) => ({
    date: new Date(row.measured_at).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }),
    systolic: row.blood_pressure_systolic ?? null,
    diastolic: row.blood_pressure_diastolic ?? null,
    temperature: row.temperature ?? null,
    heart_rate: row.heart_rate ?? null,
    sugar: row.blood_sugar ?? null,
    cholesterol: row.cholesterol ?? null,
    urid_acid: row.urid_acid ?? null,
    weight: row.weight ?? null,
    name: row.employee_name,
    raw: row,
  }));

  const getSummary = () => {
    const grouped: Record<string, typeof normalized> = {};
    normalized.forEach((row) => {
      const day = new Date(row.raw.measured_at).toLocaleDateString("id-ID");
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(row);
    });

    const summary: any[] = [];
    Object.keys(grouped).forEach((day) => {
      const rows = grouped[day];

      const metrics = [
        {
          key: "bp",
          label: "Tekanan Darah",
          allow: metric === "all" || metric === "bp",
        },
        {
          key: "temperature",
          label: "Suhu",
          allow: metric === "all" || metric === "temperature",
        },
        {
          key: "heart_rate",
          label: "Detak Jantung",
          allow: metric === "all" || metric === "heart_rate",
        },
        {
          key: "sugar",
          label: "Gula",
          allow: metric === "all" || metric === "sugar",
        },
        {
          key: "cholesterol",
          label: "Kolesterol",
          allow: metric === "all" || metric === "cholesterol",
        },
        {
          key: "urid_acid",
          label: "Asam Urat",
          allow: metric === "all" || metric === "urid_acid",
        },
        {
          key: "weight",
          label: "Berat Badan",
          allow: metric === "all" || metric === "weight",
        },
      ];

      metrics.forEach(({ key, label, allow }) => {
        if (!allow) return;

        if (key === "bp") {
          rows.forEach((r) => {
            if (
              r.systolic !== null &&
              r.systolic !== undefined &&
              r.diastolic !== null &&
              r.diastolic !== undefined
            ) {
              summary.push({
                rawDate: r.raw.measured_at,
                date: r.date,
                name: r.name,
                jenis: label,
                nilai: `${r.systolic}/${r.diastolic}`,
                sortValue: Number(r.systolic) * 1000 + Number(r.diastolic),
              });
            }
          });

          return;
        }

        rows.forEach((r) => {
          const raw = r[key as keyof typeof r];

          if (raw !== null && raw !== undefined) {
            summary.push({
              rawDate: r.raw.measured_at,
              date: r.date,
              name: r.name,
              jenis: label,
              nilai: Number(raw),
              sortValue: Number(raw),
            });
          }
        });
      });
    });

    return summary;
  };

  const summaryData = getSummary();

  const categoryOrder = [
    "Tekanan Darah",
    "Detak Jantung",
    "Suhu",
    "Gula",
    "Kolesterol",
    "Asam Urat",
    "Berat Badan",
  ];

  const sortedSummaryData = [...summaryData].sort((a, b) => {
    // 1. Jenis
    const categoryCompare =
      categoryOrder.indexOf(a.jenis) - categoryOrder.indexOf(b.jenis);

    if (categoryCompare !== 0) return categoryCompare;

    // 2. Tanggal (hanya tanggal, abaikan jam)
    const dateA = new Date(a.rawDate);
    const dateB = new Date(b.rawDate);

    const onlyDateA = new Date(
      dateA.getFullYear(),
      dateA.getMonth(),
      dateA.getDate(),
    ).getTime();

    const onlyDateB = new Date(
      dateB.getFullYear(),
      dateB.getMonth(),
      dateB.getDate(),
    ).getTime();

    if (onlyDateA !== onlyDateB) {
      return onlyDateB - onlyDateA; // tanggal terbaru dulu
    }

    // 3. Nilai
    if (mode === "highest") {
      if (a.sortValue !== b.sortValue) {
        return b.sortValue - a.sortValue;
      }
    }

    if (mode === "lowest") {
      if (a.sortValue !== b.sortValue) {
        return a.sortValue - b.sortValue;
      }
    }

    // 4. Jam (opsional)
    return dateB.getTime() - dateA.getTime();
  });

  const totalPages = Math.ceil(sortedSummaryData.length / pageSize);

  const paginatedData = sortedSummaryData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  if (loading) return <p className="text-gray-500">Loading grafik...</p>;
  if (rawData.length === 0)
    return <p className="text-gray-500">Belum ada data grafik</p>;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow">
          <p className="text-sm text-gray-500">{label}</p>
          {payload.map((p: any, i: number) => (
            <p
              key={i}
              className="text-gray-800 font-semibold"
              style={{ color: p.color }}
            >
              {p.name}: {p.value} ({p.payload.name})
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
        <h3 className="text-lg font-semibold text-gray-900">
          Grafik Kesehatan
        </h3>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Cari nama..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1 border rounded-lg text-sm"
          />

          <input
            type="month"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1 border rounded-lg text-sm"
          />

          <input
            type="month"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1 border rounded-lg text-sm"
          />

          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="px-3 py-1 border rounded-lg text-sm"
          >
            <option value="all">Semua</option>
            <option value="bp">Sistolik/Diastolik</option>
            <option value="temperature">Suhu</option>
            <option value="heart_rate">Detak Jantung</option>
            <option value="sugar">Gula</option>
            <option value="cholesterol">Kolesterol</option>
            <option value="urid_acid">Asam Urat</option>
            <option value="weight">Berat Badan</option>
          </select>

          <select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1 border rounded-lg text-sm"
          >
            <option value="all">Semua</option>
            <option value="highest">Tertinggi</option>
            <option value="lowest">Terendah</option>
          </select>

          <button
            onClick={() => {
              setSearch("");
              setStartDate("");
              setEndDate("");
              setMetric("all");
              setMode("all");
              setPage(1);
            }}
            className="px-3 py-1 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium"
          >
            Clear Filter
          </button>
        </div>
      </div>

      {mode === "all" ? (
        <>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={normalized}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#6b7280" />
              <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
              <Tooltip content={<CustomTooltip />} />

              {(metric === "all" || metric === "bp") && (
                <>
                  <Line
                    type="monotone"
                    dataKey="systolic"
                    stroke="#dc2626"
                    strokeWidth={3}
                    dot={{ fill: "#dc2626", r: 5 }}
                    name="Sistolik"
                  ></Line>
                  <Line
                    type="monotone"
                    dataKey="diastolic"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={{ fill: "#2563eb", r: 5 }}
                    name="Diastolik"
                  ></Line>
                </>
              )}

              {(metric === "all" || metric === "sugar") && (
                <Line
                  type="monotone"
                  dataKey="sugar"
                  stroke="#16a34a"
                  strokeWidth={3}
                  dot={{ fill: "#16a34a", r: 5 }}
                  name="Gula"
                ></Line>
              )}

              {(metric === "all" || metric === "cholesterol") && (
                <Line
                  type="monotone"
                  dataKey="cholesterol"
                  stroke="#9333ea"
                  strokeWidth={3}
                  dot={{ fill: "#9333ea", r: 5 }}
                  name="Kolesterol"
                ></Line>
              )}

              {(metric === "all" || metric === "urid_acid") && (
                <Line
                  type="monotone"
                  dataKey="urid_acid"
                  stroke="#f97316"
                  strokeWidth={3}
                  dot={{ fill: "#f97316", r: 5 }}
                  name="Asam Urat"
                ></Line>
              )}
              {(metric === "all" || metric === "temperature") && (
                <Line
                  type="monotone"
                  dataKey="temperature"
                  stroke="#facc15"
                  strokeWidth={3}
                  dot={{ fill: "#facc15", r: 5 }}
                  name="Suhu"
                ></Line>
              )}
              {(metric === "all" || metric === "heart_rate") && (
                <Line
                  type="monotone"
                  dataKey="heart_rate"
                  stroke="#f43f5e"
                  strokeWidth={3}
                  dot={{ fill: "#f43f5e", r: 5 }}
                  name="Detak Jantung"
                ></Line>
              )}
              {(metric === "all" || metric === "weight") && (
                <Line
                  type="monotone"
                  dataKey="weight"
                  stroke="#0ea5e9"
                  strokeWidth={3}
                  dot={{ fill: "#0ea5e9", r: 5 }}
                  name="Berat Badan"
                ></Line>
              )}
            </LineChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-2 mt-2">
            {chartLegend.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() =>
                  setMetric((prev) => (prev === item.key ? "all" : item.key))
                }
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition
        ${
          metric === item.key
            ? "bg-blue-100 border border-blue-500"
            : "hover:bg-gray-100"
        }`}
              >
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm">{item.label}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-200 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 border">Tanggal</th>
                <th className="px-4 py-2 border">Nama</th>
                <th className="px-4 py-2 border">Jenis</th>
                <th className="px-4 py-2 border">Nilai</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 border">{row.date}</td>
                  <td className="px-4 py-2 border">{row.name}</td>
                  <td className="px-4 py-2 border">{row.jenis}</td>
                  <td className="px-4 py-2 border font-semibold">
                    {row.nilai}
                  </td>
                </tr>
              ))}
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-gray-500 py-4">
                    Tidak ada data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-6">
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white shadow-sm px-3 py-2">
                {/* Previous */}
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className="px-3 py-2 text-sm rounded-lg border hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>

                {/* Nomor halaman */}
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setPage(index + 1)}
                    className={`min-w-[40px] h-10 rounded-lg text-sm font-medium transition-all ${
                      page === index + 1
                        ? "bg-blue-600 text-white shadow-md scale-105"
                        : "hover:bg-blue-50 text-gray-700"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}

                {/* Next */}
                <button
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={page === totalPages}
                  className="px-3 py-2 text-sm rounded-lg border hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HealthCharts;
